<template>
  <div class="w-full">
    <!-- Hero Detail -->
    <SectionsHeroDetail
      v-if="contactData?.heroDetail?.title"
      :key="'heroDetail'"
      :title="contactData?.heroDetail?.title"
      :description="contactData?.heroDetail?.description"
      :media="contactData?.heroDetail?.media"
      :backgroundColor="contactData?.heroDetail?.backgroundColor"
      :color="contactData?.heroDetail?.color"
      :breadcrumbs="breadcrumbs"
    />
    <!-- Hero Detail -->

    <div class="w-full pt-12 lg:pt-20 flex flex-col gap-12 lg:gap-20">
      <!-- Office Contact -->
      <section class="w-full" aria-labelledby="office-contact">
        <div class="container">
        <div
          class="w-full flex flex-col lg:flex-row gap-6 lg:gap-10 justify-center"
          :class="{ 'items-start': contactData?.contact?.length > 1 }"
        >
          <!-- Contact Info -->
          <article
            class="w-full lg:w-5/12 flex flex-col px-6 lg:px-12 py-10 lg:py-14 gap-8 relative bg-white shadow-sm"
            :class="{ 'rounded-3xl': siteSettingsStore.roundCorner }"
          >
            <div
              class="w-full z-10"
              v-for="(item, key) in contactData?.contact"
            >
              <h2
                v-if="item?.heading"
                id="office-contact"
                class="text-xl font-bold text-black mb-6 lg:mb-10"
              >
                {{ item?.heading }}
              </h2>
              <ul
                class="w-full 2xl:w-10/12 flex flex-col gap-4 lg:gap-6 font-normal"
              >
                <li v-if="item?.address" class="flex gap-4 lg:gap-5">
                  <div class="flex">
                    <svg
                      data-icon-name="place"
                      data-style="line"
                      icon_origin_id="20875"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      data-name="Line Color"
                      id="place"
                      class="icon line"
                      width="32"
                      height="32"
                    >
                      <circle
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                        r="2"
                        cy="9"
                        cx="12"
                        id="secondary"
                      ></circle>
                      <path
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                        d="M12,21S6,14,6,9A6,6,0,0,1,18,9C18,14,12,21,12,21Z"
                        id="primary"
                      ></path>
                    </svg>
                  </div>
                  <address class="not-italic">
                    {{ item?.address }}
                  </address>
                </li>
                <li v-if="item?.mail" class="flex gap-4 lg:gap-5">
                  <div class="flex">
                    <svg
                      data-icon-name="mail"
                      data-style="line"
                      icon_origin_id="16041"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      id="mail"
                      class="icon line"
                      width="32"
                      height="32"
                    >
                      <rect
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                        rx="1"
                        height="14"
                        width="18"
                        y="5"
                        x="3"
                        id="primary"
                      ></rect>
                      <polyline
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                        points="3 8 12 13 21 8"
                        data-name="primary"
                        id="primary-2"
                      ></polyline>
                    </svg>
                  </div>
                  <a
                    class="hover:text-brand-second transition-colors flex items-center"
                    :href="`mailto:${item?.mail}`"
                    rel="noopener noreferrer"
                  >
                    {{ item?.mail }}
                  </a>
                </li>
                <li v-if="item?.phone" class="flex gap-4 lg:gap-5">
                  <div class="flex">
                    <svg
                      data-icon-name="phone"
                      data-style="line"
                      icon_origin_id="16049"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      id="phone"
                      class="icon line"
                      width="32"
                      height="32"
                    >
                      <path
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                        d="M21,15v3.93a2,2,0,0,1-2.29,2A18,18,0,0,1,3.14,5.29,2,2,0,0,1,5.13,3H9a1,1,0,0,1,1,.89,10.74,10.74,0,0,0,1,3.78,1,1,0,0,1-.42,1.26l-.86.49a1,1,0,0,0-.33,1.46,14.08,14.08,0,0,0,3.69,3.69,1,1,0,0,0,1.46-.33l.49-.86A1,1,0,0,1,16.33,13a10.74,10.74,0,0,0,3.78,1A1,1,0,0,1,21,15Z"
                        id="primary"
                      ></path>
                    </svg>
                  </div>
                  <a
                    class="hover:text-brand-second transition-colors flex items-center"
                    :href="buildTelHref(item?.phone)"
                    rel="noopener noreferrer"
                  >
                    {{ item?.phone }}
                  </a>
                </li>
                <li v-if="item?.fax" class="flex gap-4 lg:gap-5">
                  <div class="flex">
                    <svg
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      class="icon line"
                      width="32"
                      height="32"
                    >
                      <rect
                        x="6"
                        y="3"
                        width="12"
                        height="5"
                        rx="1"
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                      ></rect>
                      <rect
                        x="4"
                        y="8"
                        width="16"
                        height="8"
                        rx="1.5"
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                      ></rect>
                      <rect
                        x="7"
                        y="16"
                        width="10"
                        height="5"
                        rx="1"
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                      ></rect>
                      <line
                        x1="8"
                        y1="11"
                        x2="16"
                        y2="11"
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                      ></line>
                    </svg>
                  </div>
                  <a
                    class="hover:text-brand-second transition-colors flex items-center"
                    :href="buildTelHref(item?.fax)"
                    rel="noopener noreferrer"
                  >
                    {{ item?.fax }}
                  </a>
                </li>
                <li v-if="item?.whatsapp" class="flex gap-4 lg:gap-5">
                  <div class="flex">
                    <svg
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      class="icon line w-8 h-8"
                      width="32"
                      height="32"
                    >
                      <path
                        d="M16.6,14c-0.2-0.1-1.5-0.7-1.7-0.8c-0.2-0.1-0.4-0.1-0.6,0.1c-0.2,0.2-0.6,0.8-0.8,1c-0.1,0.2-0.3,0.2-0.5,0.1c-0.7-0.3-1.4-0.7-2-1.2c-0.5-0.5-1-1.1-1.4-1.7c-0.1-0.2,0-0.4,0.1-0.5c0.1-0.1,0.2-0.3,0.4-0.4c0.1-0.1,0.2-0.3,0.2-0.4c0.1-0.1,0.1-0.3,0-0.4c-0.1-0.1-0.6-1.3-0.8-1.8C9.4,7.3,9.2,7.3,9,7.3c-0.1,0-0.3,0-0.5,0C8.3,7.3,8,7.5,7.9,7.6C7.3,8.2,7,8.9,7,9.7c0.1,0.9,0.4,1.8,1,2.6c1.1,1.6,2.5,2.9,4.2,3.7c0.5,0.2,0.9,0.4,1.4,0.5c0.5,0.2,1,0.2,1.6,0.1c0.7-0.1,1.3-0.6,1.7-1.2c0.2-0.4,0.2-0.8,0.1-1.2C17,14.2,16.8,14.1,16.6,14 M19.1,4.9C15.2,1,8.9,1,5,4.9c-3.2,3.2-3.8,8.1-1.6,12L2,22l5.3-1.4c1.5,0.8,3.1,1.2,4.7,1.2h0c5.5,0,9.9-4.4,9.9-9.9C22,9.3,20.9,6.8,19.1,4.9 M16.4,18.9c-1.3,0.8-2.8,1.3-4.4,1.3h0c-1.5,0-2.9-0.4-4.2-1.1l-0.3-0.2l-3.1,0.8l0.8-3l-0.2-0.3C2.6,12.4,3.8,7.4,7.7,4.9S16.6,3.7,19,7.5C21.4,11.4,20.3,16.5,16.4,18.9"
                        style="
                          fill: none;
                          stroke: rgb(75, 75, 75);
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-width: 0.75;
                        "
                      ></path>
                    </svg>
                  </div>
                  <a
                    class="hover:text-brand-second transition-colors flex items-center"
                    :href="buildWhatsappHref(item?.whatsapp)"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {{ item?.whatsapp }}
                  </a>
                </li>
              </ul>
              <div v-if="item?.button" class="w-full flex mt-8">
                <AtomsButton
                  :label="item?.button?.label"
                  :link="item?.button?.link"
                  :target="item?.button?.target"
                  :color="item?.button?.color"
                  :enabled="item?.button?.enabled"
                  :disabled="item?.button?.disabled"
                  :icon="item?.button?.icon"
                  :iconPosition="item?.button?.iconPosition"
                  class="w-full text-center lg:w-max uppercase tracking-widest"
                />
              </div>
            </div>
            <div
              v-if="contactData?.contact?.length < 2"
              class="hidden lg:flex absolute right-0 bottom-0 z-0 opacity-100"
            >
              <img
                src="/img/contact-img.webp"
                alt="contact-img"
                aria-label="contact-image"
                class="w-[200px] xl:w-[280px] object-contain"
                width="280"
                height="280"
              />
            </div>
          </article>

          <!-- Contact Form -->
          <article
            class="w-full lg:w-7/12 relative px-6 lg:px-12 py-10 lg:py-14 bg-brand-dark text-white shadow-xl"
            :class="{ 'rounded-3xl': siteSettingsStore.roundCorner }"
            aria-labelledby="contact-form"
          >
            <h2 id="contact-form" class="sr-only">{{ t("Contact Form") }}</h2>
            <form @submit.prevent="handleSubmit" class="relative z-10">
              <div class="w-full grid lg:grid-cols-2 gap-y-6 lg:gap-x-10">
                <AtomsFormInput
                  v-model="nameSurname"
                  :placeholder="`${$t('Name Surname')} *`"
                  name="nameSurname"
                  type="text"
                  required
                  autocomplete="name"
                  :error="!!errors.nameSurname"
                  @input="onNameInput"
                />
                <AtomsFormInput
                  v-model="phoneNumber"
                  :placeholder="`${$t('Phone Number')} *`"
                  name="phone"
                  type="tel"
                  required
                  autocomplete="tel"
                  :error="!!errors.phoneNumber"
                  @input="onPhoneInput"
                />
                <AtomsFormInput
                  v-model="email"
                  :placeholder="`${$t('Email')} *`"
                  name="email"
                  type="email"
                  required
                  autocomplete="email"
                  :error="!!errors.email"
                />
                <AtomsFormInput
                  v-model="companyName"
                  :placeholder="$t('Company Name')"
                  name="firmName"
                  type="text"
                  autocomplete="organization"
                  :error="!!errors.companyName"
                  @input="onCompanyNameInput"
                />
                <AtomsFormInput
                  v-model="subject"
                  :placeholder="`${$t('Subject')} *`"
                  name="subject"
                  type="text"
                  required
                  :error="!!errors.subject"
                  @input="onSubjectInput"
                  class="lg:col-span-2"
                />
                <AtomsFormTextarea
                  v-model="message"
                  :placeholder="`${$t('Message')} *`"
                  name="message"
                  required
                  height="60"
                  :error="!!errors.message"
                  class="lg:col-span-2"
                />
              </div>
              <div class="w-full mt-8">
                <AtomsFormCheck
                  v-model="checked"
                  :label="`${$t('Kvkk')} *`"
                  name="kvkk"
                  :modal="true"
                  :error="!!errors.checked"
                />
              </div>
              <div class="w-full flex justify-end mt-4">
                <AtomsButton
                  :label="$t('Submit')"
                  type="submit"
                  color="white"
                  :enabled="true"
                  :disabled="isFormDisabled"
                  class="w-full lg:w-max uppercase tracking-widest"
                  @click="submitForm"
                />
              </div>
            </form>
          </article>
        </div>
      </div>
    </section>

    <!-- Location Map -->
    <section v-if="contactData?.mapIframe" class="w-full">
      <div class="container">
        <div
          class="w-full bg-white p-4 lg:p-5 shadow-sm"
          :class="{ 'rounded-3xl': siteSettingsStore.roundCorner }"
        >
          <div class="w-full aspect-[4/3] lg:aspect-[21/9] overflow-hidden rounded-2xl">
            <iframe
              :title="contactData?.contact?.heading"
              :src="contactData?.mapIframe"
              width="100%"
              height="100%"
              style="border: 0"
              allowfullscreen
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              class="flex w-full h-full"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
    </div>
  </div>
</template>

<script setup>
definePageMeta({ name: "contact" });
/* -------------------- 📦 Importlar -------------------- */
import { useI18n } from "vue-i18n";
import { useMetaTags } from "~/composables/headConfig";
import { useRoute } from "vue-router";
import { useApi } from "~/server/getApi";
import { usePostApi } from "~/server/postApi";
import { useSiteSettingsStore } from "@/store/index";
import { useRecaptcha } from "~/composables/useRecaptcha";
import {
  buildJsonLdScripts,
  createWebPageSchema,
  stripHtmlTags,
} from "~/composables/schemaOrg";

/* -------------------- 🌐 Global Tanımlar -------------------- */
const { t, locale } = useI18n();
const toast = useToast();
const route = useRoute();
const config = useRuntimeConfig();
const siteUrl = config.public.siteUrl;
const baseUrl = config.public.baseUrl;
const fullUrl = computed(() => `${siteUrl}${route.fullPath}`);
const siteSettingsStore = useSiteSettingsStore();
const recaptchaToken = computed(() => siteSettingsStore.recaptchaToken);
const { loadRecaptcha } = useRecaptcha();

/* -------------------- 📡 API ile Sayfa Verisini Çek -------------------- */
const { data: contactData, fetchData: fetchContactData } =
  useApi(`contact?&pLevel`);

const { error: contactPostError, fetchData: fetchContactPostData } =
  usePostApi(`contact-forms`);

await fetchContactData();
if (contactData.value) {
  siteSettingsStore.setFooterContact(contactData.value, locale.value);
}

/* -------------------- 🔖 Breadcrumb Tanımı -------------------- */
const breadcrumbs = computed(() => [
  {
    enabled: true,
    link: locale.value === "tr" ? "/" : `/${locale.value}`,
    title: `
      <svg xmlns="http://www.w3.org/2000/svg" 
           fill="none" 
           viewBox="0 0 24 24" 
           stroke-width="1.5" 
           stroke="currentColor" 
           class="w-4 h-4 md:w-5 md:h-5">
        <path stroke-linecap="round" 
              stroke-linejoin="round" 
              d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 
              .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 
              0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 
              1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    `,
  },
  {
    enabled: true,
    title: contactData?.value?.heroDetail?.title,
  },
]);

const normalizePhoneNumber = (value) => String(value || "").replace(/\D+/g, "");
const toInternationalPhone = (value) => {
  const digits = normalizePhoneNumber(value);
  if (!digits) return "";
  if (digits.startsWith("90")) return digits;
  if (digits.startsWith("0")) return `9${digits}`;
  if (digits.startsWith("9")) return digits;
  return `90${digits}`;
};
const buildTelHref = (value) => `tel:+${toInternationalPhone(value)}`;
const buildWhatsappHref = (value) =>
  `https://wa.me/${toInternationalPhone(value)}`;

/* -------------------- 🔧 SEO Etiketi -------------------- */
const seoData = computed(() => contactData.value?.seo ?? {});
const pageTitle = computed(() => contactData.value?.heroDetail?.title);
const pageDescription = computed(
  () =>
    contactData.value?.heroDetail?.description ||
    contactData.value?.description,
);

useMetaTags(seoData, pageTitle, pageDescription);

useHead(() => {
  const contact = contactData.value;
  if (!contact) {
    return { script: [] };
  }

  const contactEntries = Array.isArray(contact?.contact)
    ? contact.contact.filter(Boolean)
    : [];

  const primaryContact =
    contactEntries.find(
      (entry) => entry?.address || entry?.phone || entry?.mail,
    ) ??
    contactEntries[0] ??
    {};

  const socialLinks = gatherSocialLinks(
    contact?.socialMedia,
    contact?.socialLinks,
    primaryContact?.socialMedia,
    siteSettingsStore.socialMedia,
  );

  const address = {
    "@type": "PostalAddress",
    streetAddress: primaryContact?.address || "",
    addressLocality: contact?.city || "İstanbul",
    addressRegion: contact?.region || "İstanbul",
    postalCode: contact?.postalCode || "",
    addressCountry: contact?.country || "TR",
  };

  const contactPoint = {
    "@type": "ContactPoint",
    telephone: primaryContact?.phone || siteSettingsStore.phone || "",
    contactType: primaryContact?.heading || "customer support",
    email: primaryContact?.mail || "",
    availableLanguage: [locale.value],
    areaServed: contact?.areaServed || "TR",
  };

  const contactSchema = createWebPageSchema({
    type: "ContactPage",
    url: fullUrl.value,
    name: contact.heroDetail?.title || contact.title,
    headline: contact.heroDetail?.title || contact.title,
    description:
      contact.seo?.description ||
      contact.heroDetail?.description ||
      contact.description,
    publishedAt: contact.publishedAt,
    updatedAt: contact.updatedAt,
    keywords: contact.seo?.keywords,
    baseUrl,
    mainEntityType: "ContactPage",
  });

  if (contactSchema) {
    contactSchema.address = address;
    contactSchema.contactPoint = contactPoint;
    if (socialLinks.length) {
      contactSchema.sameAs = socialLinks;
    }
  }

  return {
    script: buildJsonLdScripts([
      {
        key: "schema-contact-page",
        schema: contactSchema,
      },
    ]),
  };
});

/* -------------------- 📝 Form Alanları -------------------- */
const isFormDisabled = ref(false);
const nameSurname = ref("");
const phoneNumber = ref("");
const email = ref("");
const companyName = ref("");
const subject = ref("");
const message = ref("");
const checked = ref(false);

const errors = ref({
  nameSurname: "",
  phoneNumber: "",
  email: "",
  companyName: "",
  subject: "",
  message: "",
  checked: false,
});

/* -------------------- ✅ Form Validasyonu -------------------- */
function validateForm() {
  let valid = true;
  // Reset errors before validation
  errors.value = {
    nameSurname: "",
    phoneNumber: "",
    email: "",
    companyName: "",
    subject: "",
    message: "",
    checked: false,
  };

  const nameRegex = /^[a-zA-ZçÇğĞıİöÖşŞüÜ\s'-]+$/;
  const companyRegex = /^[a-zA-Z0-9çÇğĞıİöÖşŞüÜ\s'&.,#()-]+$/;
  const subjectRegex = /^[a-zA-Z0-9çÇğĞıİöÖşŞüÜ\s'&.,#?!()-]+$/;
  const phoneRegex = /^(?:0\d{10}|\d{10})$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (
    !nameSurname.value ||
    nameSurname.value.length < 3 ||
    !nameRegex.test(nameSurname.value)
  ) {
    errors.value.nameSurname = "Name Surname Valid";
    valid = false;
  }

  if (
    companyName.value &&
    (companyName.value.length < 3 || !companyRegex.test(companyName.value))
  ) {
    errors.value.companyName = "Company Name Valid";
    valid = false;
  }

  if (
    !subject.value ||
    subject.value.length < 3 ||
    !subjectRegex.test(subject.value)
  ) {
    errors.value.subject = "Subject Valid";
    valid = false;
  }

  if (
    !message.value ||
    message.value.length < 3 ||
    message.value.length > 300
  ) {
    errors.value.message = "Message Valid";
    valid = false;
  }

  if (!phoneNumber.value || !phoneRegex.test(phoneNumber.value)) {
    errors.value.phoneNumber = "Phone Number Valid";
    valid = false;
  }

  if (!email.value || !emailRegex.test(email.value)) {
    errors.value.email = "Email Valid";
    valid = false;
  }

  if (!checked.value) {
    errors.value.checked = "Policy Valid";
    valid = false;
  }

  return valid;
}

/* -------------------- ✅ Mask -------------------- */
function onNameInput(e) {
  const onlyLetters = e.target.value
    .replace(/[^a-zA-ZçÇğĞıİöÖşŞüÜ\s'-]/g, "")
    .slice(0, 30);
  nameSurname.value = onlyLetters;
}

function onSubjectInput(e) {
  const sanitized = e.target.value
    .replace(/[^a-zA-Z0-9çÇğĞıİöÖşŞüÜ\s'&.,#?!()-]/g, "")
    .slice(0, 60);
  subject.value = sanitized;
}

function onCompanyNameInput(e) {
  const sanitized = e.target.value
    .replace(/[^a-zA-Z0-9çÇğĞıİöÖşŞüÜ\s'&.,#()-]/g, "")
    .slice(0, 60);
  companyName.value = sanitized;
}

function onPhoneInput(e) {
  const digits = e.target.value.replace(/\D/g, "");
  const limited = digits.startsWith("0")
    ? digits.slice(0, 11)
    : digits.slice(0, 10);
  phoneNumber.value = limited;
}

/* -------------------- 📤 Form Gönderme -------------------- */
async function handleSubmit() {
  // Alias for button @click compatibility
  await submitForm();
}

async function submitForm() {
  if (!validateForm()) return;
  
  // Clear previous error state
  if (contactPostError.value) {
    contactPostError.value = null;
  }
  
  try {
    if (!recaptchaToken.value) {
      toast.add({
        severity: "error",
        summary: t("Error"),
        detail: t("Recaptcha Failed"),
        life: 5000,
      });
      return;
    }

    isFormDisabled.value = true;
    
    let recaptcha;
    try {
      recaptcha = await loadRecaptcha(recaptchaToken.value);
    } catch (recaptchaLoadError) {
      isFormDisabled.value = false;
      toast.add({
        severity: "error",
        summary: t("Error"),
        detail: t("Recaptcha Failed"),
        life: 5000,
      });
      return;
    }

    let token;
    try {
      token = await new Promise((resolve, reject) => {
        recaptcha.ready(() => {
          recaptcha
            .execute(recaptchaToken.value, { action: "submit" })
            .then(resolve)
            .catch(reject);
        });
      });
    } catch (tokenError) {
      isFormDisabled.value = false;
      toast.add({
        severity: "error",
        summary: t("Error"),
        detail: t("Recaptcha Failed"),
        life: 5000,
      });
      return;
    }

    if (!token) {
      isFormDisabled.value = false;
      toast.add({
        severity: "error",
        summary: t("Error"),
        detail: t("Recaptcha Failed"),
        life: 5000,
      });
      return;
    }

    const payload = {
      nameSurname: nameSurname.value,
      phoneNumber: phoneNumber.value,
      email: email.value,
      companyName: companyName.value,
      subject: subject.value,
      message: message.value,
      recaptchaToken: token,
    };
    await fetchContactPostData(payload);

    if (!contactPostError.value) {
      toast.add({
        severity: "success",
        summary: t("Successful"),
        detail: t("Success Message"),
        life: 5000,
      });

      // Form başarılıysa temizle
      nameSurname.value = "";
      phoneNumber.value = "";
      email.value = "";
      companyName.value = "";
      subject.value = "";
      message.value = "";
      checked.value = false;
    } else {
      toast.add({
        severity: "error",
        summary: t("Error"),
        detail: typeof contactPostError.value === "string" 
          ? contactPostError.value : t("Form Error"),
        life: 5000,
      });
    }
  } catch (error) {
    toast.add({
      severity: "error",
      summary: t("Error"),
      detail: t("Form Error"),
      life: 5000,
    });
  } finally {
    isFormDisabled.value = false;
  }
}
function gatherSocialLinks(...sources) {
  const seen = new Set();
  const urlPattern = /^https?:\/\//i;

  const visit = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (urlPattern.test(trimmed)) {
        seen.add(trimmed);
      }
      return;
    }
    if (typeof value === "object") {
      Object.values(value).forEach(visit);
    }
  };

  sources.forEach(visit);
  return Array.from(seen);
}

/* -------------------- 📤 UseHead Scheme -------------------- */
useHead(() => {
  const contactDataScheme = contactData.value;
  if (!contactDataScheme) {
    return { script: [] };
  }

  const contactEntries = Array.isArray(contactDataScheme?.contact)
    ? contactDataScheme.contact.filter(Boolean)
    : [];

  const primaryContact =
    contactEntries.find(
      (entry) => entry?.address || entry?.phone || entry?.mail,
    ) ??
    contactEntries[0] ??
    {};

  const socialLinks = gatherSocialLinks(
    contactDataScheme?.socialMedia,
    contactDataScheme?.socialLinks,
    primaryContact?.socialMedia,
    siteSettingsStore.socialMedia,
  );

  const contactSchema = createWebPageSchema({
    type: "ContactPage",
    url: fullUrl.value,
    name: contactDataScheme?.heroDetail?.title,
    headline: contactDataScheme?.heroDetail?.title,
    description:
      contactDataScheme?.seo?.description ||
      contactDataScheme?.heroDetail?.description ||
      contactDataScheme?.description,
    publishedAt: contactDataScheme?.publishedAt,
    updatedAt: contactDataScheme?.updatedAt,
    keywords: contactDataScheme?.seo?.keywords,
    baseUrl,
    sameAs: socialLinks.length ? socialLinks : undefined,
  });

  return {
    script: buildJsonLdScripts([
      {
        key: "schema-contact-page",
        schema: contactSchema,
      },
    ]),
  };
});
</script>
